import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiClient } from '../api/client'
import { FollowButton } from '../components/FollowButton'
import { trackBusinessEvent } from '../utils/analytics'
import { useLanguage } from '../hooks/useLanguage'

interface PredictionMarket {
  pm_index: string
  pm_id: string
  pm_slug: string
  pm_title: string
  confidence: number
  match_type: string
  reason: string
}

interface MarketMatchedEvent {
  event_id: string
  event_title: string
  matched_pm_markets: PredictionMarket[]
}

interface LatestMarketsResponse {
  results: MarketMatchedEvent[]
}

interface EngagementStats {
  likes?: number
  retweets?: number
  views?: number
}

interface Web3EventDetail {
  event_id: string
  event_instance_id?: string
  title?: string
  narrative: string
  narrative_name?: string
  background?: string
  what_happened?: string
  total_mentions?: number
  engagement?: EngagementStats
}

function formatConfidence(confidence: number) {
  return `${Math.round((confidence || 0) * 100)}%`
}

function formatMatchType(matchType: string) {
  const names: Record<string, string> = {
    same_event: 'Same Event',
    same_specific_event: 'Same Event',
    same_event_chain: 'Event Chain',
    same_company_event_chain: 'Company Event Chain',
    event_impact: 'Event Impact',
    direct_impact: 'Direct Impact',
    same_asset: 'Same Asset',
    same_token: 'Same Asset',
    same_company: 'Same Company',
    same_project: 'Same Project',
    same_entity: 'Same Entity',
    same_specific_entity: 'Same Entity',
    macro_narrative: 'Macro Narrative',
    same_narrative: 'Same Narrative',
  }
  return names[matchType] || matchType.replace(/_/g, ' ')
}

function getPolymarketUrl(slug: string) {
  return `https://polymarket.com/event/${slug}`
}

function formatNum(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return String(num)
}

function getDateFromEventId(eventId: string) {
  const match = eventId.match(/^(\d{4}\.\d{2}\.\d{2})_/)
  return match?.[1] || 'latest'
}

export function MarketDetailPage() {
  const { eventId = '' } = useParams()
  const navigate = useNavigate()
  const { lang, t } = useLanguage()
  const decodedEventId = decodeURIComponent(eventId)
  const [event, setEvent] = useState<Web3EventDetail | null>(null)
  const [markets, setMarkets] = useState<PredictionMarket[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function loadEvent() {
      setIsLoading(true)
      setError(null)
      try {
        const [response, marketsResponse] = await Promise.all([
          apiClient.get<Web3EventDetail>(`/api/events/${decodedEventId}`, { lang }),
          apiClient.get<LatestMarketsResponse>('/api/polymarket/latest', { lang }),
        ])
        if (cancelled) return
        setEvent(response)
        const matchedEvent = marketsResponse.results.find((item) => item.event_id === decodedEventId)
        setMarkets(matchedEvent?.matched_pm_markets || [])
        trackBusinessEvent('market_detail_loaded', {
          event_id: response.event_id,
          narrative: response.narrative,
          source_page: 'prediction_market_detail',
        }, response.event_id)
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load event detail')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadEvent()
    return () => {
      cancelled = true
    }
  }, [decodedEventId, lang])

  const date = getDateFromEventId(decodedEventId)

  const handleOpenExplain = () => {
    if (!event) return
    trackBusinessEvent('market_detail_explain_click', {
      event_id: event.event_id,
      narrative: event.narrative,
      source_page: 'prediction_market_detail',
    }, event.event_id)
    navigate(`/event/${event.event_id}/explain?date=${date}&narrative=${event.narrative}&from=market_detail`)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 z-20">
        <div className="max-w-3xl mx-auto flex items-center gap-3 p-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200"
            aria-label={t('返回', 'Back')}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white truncate">
            {t('预测市场详情', 'Prediction Markets Detail')}
          </h1>
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-4 p-4">
        {isLoading && (
          <div className="rounded-lg border border-gray-200 bg-white p-6 text-center text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
            {t('正在加载事件详情...', 'Loading event detail...')}
          </div>
        )}

        {!isLoading && error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
            {error}
          </div>
        )}

        {!isLoading && !error && event && (
          <>
            <section className="rounded-xl bg-white p-4 dark:bg-gray-800">
              <h2 className="text-lg font-bold leading-snug text-gray-900 dark:text-white">
                {event.title || t('未命名事件', 'Untitled Event')}
              </h2>
            </section>

            {event.background && (
              <section className="rounded-xl bg-white p-4 dark:bg-gray-800">
                <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  <span>📖</span>
                  <span>{t('背景', 'Background')}</span>
                </h3>
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  {event.background}
                </p>
              </section>
            )}

            {event.what_happened && (
              <section className="rounded-xl bg-white p-4 dark:bg-gray-800">
                <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                  <span>📝</span>
                  <span>{t('事件详情', 'What Happened')}</span>
                </h3>
                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                  {event.what_happened}
                </p>
              </section>
            )}

            <section className="rounded-xl bg-white p-4 dark:bg-gray-800">
              <div className="grid grid-cols-4 gap-2">
                <div className="rounded-lg bg-red-50 p-2 text-center dark:bg-red-900/20">
                  <div className="text-lg font-bold text-red-600">{formatNum(event.engagement?.likes || 0)}</div>
                  <div className="text-[10px] text-gray-500">Likes</div>
                </div>
                <div className="rounded-lg bg-green-50 p-2 text-center dark:bg-green-900/20">
                  <div className="text-lg font-bold text-green-600">{formatNum(event.engagement?.retweets || 0)}</div>
                  <div className="text-[10px] text-gray-500">Retweets</div>
                </div>
                <div className="rounded-lg bg-purple-50 p-2 text-center dark:bg-purple-900/20">
                  <div className="text-lg font-bold text-purple-600">{formatNum(event.engagement?.views || 0)}</div>
                  <div className="text-[10px] text-gray-500">Views</div>
                </div>
                <div className="rounded-lg bg-blue-50 p-2 text-center dark:bg-blue-900/20">
                  <div className="text-lg font-bold text-blue-600">{event.total_mentions || 0}</div>
                  <div className="text-[10px] text-gray-500">Mentions</div>
                </div>
              </div>
            </section>

            <section className="flex gap-3">
              <FollowButton
                eventId={event.event_id}
                date={date}
                eventTitle={event.title || ''}
                narrativeName={event.narrative_name || event.narrative}
                eventInstanceId={event.event_instance_id}
                eventData={event as unknown as Record<string, unknown>}
              />
              <button
                type="button"
                onClick={handleOpenExplain}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <span>💬</span>
                <span>{t('展开说说', 'Explain')}</span>
              </button>
            </section>

            {markets.length > 0 && (
              <section className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {t('相关预测市场', 'Related Prediction Markets')} ({markets.length})
                </h3>
                {markets.map((market) => (
                  <article
                    key={`${market.pm_id}-${market.pm_index}`}
                    className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                  >
                    <div className="mb-2 flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className="rounded-md bg-emerald-50 px-2 py-1 font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200">
                        {formatConfidence(market.confidence)}
                      </span>
                      <span className="rounded-md bg-gray-100 px-2 py-1 dark:bg-gray-700">
                        {formatMatchType(market.match_type)}
                      </span>
                    </div>
                    <a
                      href={getPolymarketUrl(market.pm_slug)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-sm font-semibold leading-relaxed text-blue-600 hover:underline dark:text-blue-300"
                    >
                      {market.pm_title}
                    </a>
                    <p className="mt-3 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                      <span className="font-semibold text-gray-700 dark:text-gray-200">Reason: </span>
                      {market.reason}
                    </p>
                  </article>
                ))}
              </section>
            )}
          </>
        )}
      </main>
    </div>
  )
}
